import has from '@atomicservice/ascf-web-sdk';
import * as Sentry from '@sentry/react';
import { isProd } from './context';

const WECHAT_PROD = 'wechat_pay'; // wechat_pay|ali_pay
const WECHAT_DEV = 'wechat_pay_dev'; // wechat_pay_dev|ali_pay_dev

// 生产环境用 wechat_pay|ali_pay，测试环境用带 _dev 后缀的支付方式
const selectPayType = isProd ? WECHAT_PROD : WECHAT_DEV;

// 元服务订阅消息模板 id（元服务一站式平台申请），需与后端发送通知时使用的模板 id 一致，一次最多 3 个
const ASCF_SUBSCRIBE_TEMPLATE_MAX = 3;
const ASCF_SUBSCRIBE_TEMPLATE_IDS: string[] = (
  import.meta.env.VITE_APP_ASCF_SUBSCRIBE_TEMPLATE_IDS || ''
)
  .split(',')
  .map((id: string) => id.trim())
  .filter(Boolean)
  .slice(0, ASCF_SUBSCRIBE_TEMPLATE_MAX);

interface AscfPaymentParams {
  wechatPaymentUrl?: string; // 后端返回的微信 Linkpay 三方支付(H5)链接
  alipayPaymentUrl?: string; // 后端返回的支付宝 Linkpay 三方支付(H5)链接
  amount?: number; // 收银台展示金额，单位为分
  currency?: string;
  tradeSummary?: string;
  onSuccess?: (res?: unknown) => void;
  onFail?: (error?: unknown) => void;
}

/**
 * https://developer.huawei.com/consumer/cn/doc/atomic-ascf/components-web-view#hasrequestpayment
 * 1.拿到第一步cashierPicker返回的clientToken后，组装跳转信息调用
 * 2.requestPayment,由华为容器唤起三方app(微信/支付宝)
 * 3.nextAction=L 表示走 linkUrl返回的url
 * 4.H5支付场景 payload 固定传 "AP"
 */
const jumpToThirdPartyPayment = (
  paymentUrl: string,
  clientToken: string,
  onSuccess?: (res?: unknown) => void,
  onFail?: (error?: unknown) => void
) => {
  const orderStr = JSON.stringify({
    nextAction: 'L',
    linkUrl: paymentUrl,
    clientToken
  });

  has.requestPayment({
    orderStr,
    payload: 'AP',
    success: res => {
      onSuccess?.(res);
    },
    fail: err => {
      onFail?.(err);
    }
  });
};

/**
 * 元服务 ASCF 环境下向用户发起消息订阅，用户订阅后后端才能下发通知
 * https://developer.huawei.com/consumer/cn/doc/atomic-ascf/apis-message-subscription
 * 订阅结束（无论成功与否）后执行 onComplete，不阻塞后续流程
 */
export const requestAscfSubscribeMessage = (onComplete?: () => void) => {
  if (!ASCF_SUBSCRIBE_TEMPLATE_IDS.length) {
    onComplete?.();
    return;
  }

  has.requestSubscribeMessage({
    tmplIds: ASCF_SUBSCRIBE_TEMPLATE_IDS,
    fail: err => {
      console.error('[ascfSubscribe] fail', JSON.stringify(err));
      Sentry.captureMessage(
        `[ascfSubscribe] fail: ${JSON.stringify(err)}`,
        'warning'
      );
    },
    complete: () => {
      onComplete?.();
    }
  });
};

/**
 * 元服务 ASCF 环境下拉起微信、支付宝支付
 * 1. cashierPicker 让用户选择支付方式，返回 clientToken
 * 2. requestPayment带上clientToken组装跳转信息,直接跳转
 */
export const requestAscfPayment = ({
  wechatPaymentUrl,
  alipayPaymentUrl,
  amount = 0,
  currency,
  tradeSummary,
  onSuccess,
  onFail
}: AscfPaymentParams) => {
  has.cashierPicker({
    tradeSummary,
    amount,
    extraInfo: JSON.stringify({
      selectPayType
    }),
    currency,
    success: res => {
      const clientToken = res?.clientToken;
      if (!clientToken) {
        onFail?.(res);
        return;
      }
      const selectedPaymentType = res?.selectedPaymentType ?? '';
      const isWechatSelected = selectedPaymentType.includes('wechat');
      const selectedPaymentUrl = isWechatSelected
        ? wechatPaymentUrl
        : alipayPaymentUrl;
      if (!selectedPaymentUrl) {
        onFail?.(res);
        return;
      }
      jumpToThirdPartyPayment(
        selectedPaymentUrl,
        clientToken,
        onSuccess,
        onFail
      );
    },
    fail: err => {
      onFail?.(err);
    }
  });
};
